import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Loader2, MapPin, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { HotelVendorBadge } from '@/components/hotels/hotel-vendor-badge';
import { PnrSourceMark } from '@/components/provenance/pnr-source-mark';
import { cn } from '@/lib/cn';
import { searchHotels } from '@/lib/allocation/api';
import { stayWindow, windowAsPlan } from '@/lib/allocation/stay';
import { useGroupStay, useSetGroupNights } from '@/lib/allocation/use-stay';
import { StayLine, StayMismatchNote } from '@/components/allocation/stay-line';
import { Unpriced } from '@/components/allocation/unpriced';
import { isPriced } from '@/lib/allocation/price';
import type { HotelOption } from '@/lib/allocation/types';
import { bookReservation, holdOffer } from '@/lib/booking/api';
import { OfferExpiredError } from '@/lib/booking/errors';
import { newIdempotencyKey } from '@/lib/uuid';
import { LockConflictError } from '@/lib/locks/types';
import { useAllocateCaseHotel } from '@/lib/disruptions/use-disruptions';
import type { DisruptionPnrGroupRow } from '@/lib/disruptions/types';
import { priorityLabel } from '../labels';
import type { DeskRow } from './desk-model';
import { OutcomeDialog, type OutcomePath } from './outcome-dialog';

// The desk's right half: resolve ONE PNR, with context. Header says who and why;
// the offers list is the main body (live, policy-ranked, one click = reversible
// HOLD); the alternatives ladder sits at the bottom, once — quiet until needed,
// loud when the search comes back empty. Solving here auto-advances the queue
// (the parent re-selects the next open PNR when this one leaves the lane).

export function ResolvePanel({
  desk,
  disruptionUrn,
}: {
  desk: DeskRow;
  disruptionUrn: string;
}) {
  const { t } = useTranslation('tower');
  const { row } = desk;
  const qc = useQueryClient();
  const [held, setHeld] = useState<string | null>(null);
  const [outcomePath, setOutcomePath] = useState<OutcomePath | null>(null);

  // How long this PNR is actually stranded (feature `stay-nights`, N8). Offers are
  // priced `perNight × nights` and availability is checked against real dates, so
  // the search has to state the group's own window — the desk used to send a
  // hardcoded `now → now + 24h` and quote a four-night stay as one.
  const groupStay = useGroupStay(row.caseUrn, row.subCaseUrn);
  const stay = useMemo(() => stayWindow(groupStay.stayPlan), [groupStay.stayPlan]);
  const setNights = useSetGroupNights({ disruptionUrn }, row.subCaseUrn);
  const airportCode = row.overnightStation ?? row.origin;
  const guests = Math.max(1, row.paxBreakdown.adt + row.paxBreakdown.chd);

  const offers = useQuery<HotelOption[]>({
    // The window is part of the cache key: re-derive the nights and the previous
    // one-night quotes must not be served for the new stay.
    queryKey: ['hotel-desk', 'offers', row.subCaseUrn, airportCode, guests, stay.checkInUtc, stay.checkOutUtc],
    queryFn: () =>
      searchHotels({
        caseUrn: row.caseUrn,
        airportCode,
        guests,
        stay,
      }),
    staleTime: 60_000,
    enabled: !groupStay.isLoading && !groupStay.isError,
  });

  // Rooms the GROUP needs (~2 pax/room, min 1) — the planner's own formula;
  // `offerCapacity` is rooms REQUESTED on the hold wire.
  const roomsNeeded = Math.max(1, Math.ceil(guests / 2));

  // The proven manual seam (use-holds confirm): soft-hold the offer, then book it
  // in one motion. The booking lands async (PENDING → confirmed) and the cases
  // denorm flips the roster row — which is what pops this PNR off the queue.
  const hold = useMutation({
    mutationFn: async (offer: HotelOption) => {
      // The stay the offer was QUOTED for — booking prices from that quote (§6.15).
      const quoted = offer.searchedStay ?? stay;
      const held = await holdOffer({
        hotelName: offer.name,
        checkInUtc: quoted.checkInUtc,
        checkOutUtc: quoted.checkOutUtc,
        offerUrn: offer.offerUrn,
        caseUrn: row.caseUrn,
        subCaseUrn: row.subCaseUrn,
        offerCapacity: roomsNeeded,
      });
      // Book the window we SEARCHED and the offer we showed. No amount goes back:
      // the rate on screen is the airline's PRICE, and booking prices the
      // reservation from the offer itself, whole stay (commercial-contract D16,
      // §6.15) — a price never travels back to booking as Nexa's cost.
      return bookReservation(
        {
          caseUrn: row.caseUrn,
          subCaseUrn: row.subCaseUrn,
          offerUrn: offer.offerUrn,
          attemptUrn: held.attemptUrn,
          hotelUrn: offer.hotelUrn,
          hotelName: offer.name,
          // B1 (#820): the offer's coordinates ride onto the reservation so the
          // transport voucher fence can resolve the booked hotel; never invented.
          ...(offer.geo ? { hotel: { name: offer.name, geo: offer.geo } } : {}),
          rooms: roomsNeeded,
          guests,
          checkInUtc: quoted.checkInUtc,
          checkOutUtc: quoted.checkOutUtc,
          currency: 'USD',
        },
        newIdempotencyKey(),
      );
    },
    onSuccess: (_res, offer) => {
      setHeld(offer.offerUrn);
      // The denorm + WS flip the roster row; nudge it so the queue advances
      // without waiting for the push.
      void qc.invalidateQueries({ queryKey: ['disruptions', disruptionUrn, 'roster'] });
      void qc.invalidateQueries({ queryKey: ['cases', row.caseUrn, 'reservations'] });
    },
  });

  const autoAllocate = useAllocateCaseHotel({ kind: 'disruption', disruptionUrn });

  const holdError =
    // booking@v2 has no live quote for this offer + stay (409 `offer_expired`): say so —
    // the operator searches again; nothing re-searches behind their back.
    hold.error instanceof OfferExpiredError
      ? t('caseWork:holds.offerExpired')
      : hold.error instanceof LockConflictError
      ? t('desk.resolve.holdConflict', {
          operator: hold.error.holder?.displayName ?? t('common:holds.lock.anotherOperator'),
        })
      : hold.isError
        ? t('desk.resolve.holdError')
        : null;

  const searchPending = groupStay.isLoading || offers.isLoading;
  const searchFailed = groupStay.isError || offers.isError;
  const noInventory = !searchPending && !searchFailed && (offers.data ?? []).length === 0;

  return (
    <section
      className="flex min-h-0 flex-col rounded-lg border border-border bg-card"
      data-testid="hotel-picker"
      aria-label={t('desk.resolve.aria', { pnr: row.pnrLabel })}
    >
      {/* Who + why — the context the actions below act on. */}
      <header className="border-b border-border px-4 py-3">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="font-mono text-lg font-bold">{row.pnrLabel}</span>
          <PnrSourceMark source={row.pnrSource} className="self-center" testId="hotel-picker-source" />
          <span className="text-sm text-muted-foreground">
            {t('desk.resolve.paxFlight', { pax: row.paxCount, flight: row.flightLabel })}
          </span>
          {desk.lane === 'DECLINED' ? (
            <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[11px] font-semibold text-rose-800 dark:bg-rose-900/40 dark:text-rose-200">
              {t('desk.resolve.declinedBadge')}
            </span>
          ) : null}
        </div>
        {(row.priorityTags ?? []).length > 0 ? (
          <p className="mt-1.5 flex flex-wrap gap-1.5">
            {(row.priorityTags ?? []).map((tag) => (
              <span
                key={tag}
                className="rounded bg-violet-100 px-1.5 py-0.5 text-[11px] font-medium text-violet-800 dark:bg-violet-900/40 dark:text-violet-200"
              >
                {priorityLabel(tag, t)}
              </span>
            ))}
          </p>
        ) : null}
        {/* HOW LONG — the number every price below is multiplied by. */}
        <p className="mt-1.5 text-xs" data-testid="resolve-stay">
          {groupStay.isLoading ? (
            <span className="text-muted-foreground">{t('desk.resolve.stayLoading')}</span>
          ) : groupStay.isError ? null : (
            <StayLine plan={groupStay.stayPlan ?? windowAsPlan(stay)} />
          )}
        </p>
        <StayMismatchNote
          mismatch={groupStay.stayMismatch}
          onResolve={(n) => setNights.mutate(n)}
          pending={setNights.isPending}
          className="mt-2"
        />
      </header>

      {/* The offers — the main body. One click holds; the hold is reversible. */}
      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-4">
        {searchPending ? (
          <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />{' '}
            {t('desk.resolve.searching', { airport: airportCode })}
          </p>
        ) : searchFailed ? (
          <p className="py-6 text-sm text-destructive">
            {t('desk.resolve.searchFailed')}
          </p>
        ) : noInventory ? (
          <div
            className="rounded-md border border-amber-300 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/30"
            data-testid="picker-no-inventory"
          >
            <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
              {t('desk.resolve.noInventoryTitle', { airport: airportCode })}
            </p>
            <p className="mt-1.5 text-xs leading-5 text-amber-800/80 dark:text-amber-200/80">
              {t('desk.resolve.noInventoryBody', { pax: row.paxCount })}
            </p>
          </div>
        ) : (
          [...(offers.data ?? [])]
            .sort((a, b) => a.distanceKm - b.distanceKm)
            .map((offer) => {
            const isHeld = held === offer.offerUrn;
            return (
              <div
                key={offer.offerUrn}
                className={cn(
                  'flex items-center gap-4 rounded-md border px-3 py-2.5',
                  isHeld
                    ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-950/30'
                    : 'border-border',
                )}
                data-testid={`picker-offer-${offer.hotelUrn}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-1.5 text-sm font-medium">
                    <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                    <span className="truncate">{offer.name}</span>
                    <HotelVendorBadge urn={offer.hotelUrn} className="shrink-0" />
                  </p>
                  <p className="mt-1 flex flex-wrap items-center gap-x-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-0.5">
                      <Star className="h-3 w-3" aria-hidden /> {offer.stars || '—'}
                    </span>
                    <span className="flex items-center gap-0.5">
                      <MapPin className="h-3 w-3" aria-hidden /> {offer.distanceKm.toFixed(1)} km
                    </span>
                    {(stay.nights ?? 1) > 1 && isPriced(offer.ratePerRoomUSD) ? (
                      <span className="tabular-nums font-medium text-foreground">
                        {t('desk.resolve.stayTotal', {
                          count: stay.nights ?? 1,
                          total: Math.round(offer.ratePerRoomUSD * roomsNeeded * (stay.nights ?? 1) * 100) / 100,
                        })}
                      </span>
                    ) : null}
                    <span className="tabular-nums">
                      {t('desk.resolve.roomsFree', { count: offer.availableRooms })}
                    </span>
                  </p>
                </div>
                <div className="w-20 shrink-0 text-right">
                  <p className="text-sm font-semibold tabular-nums">
                    {isPriced(offer.ratePerRoomUSD) ? `$${offer.ratePerRoomUSD}` : <Unpriced className="text-xs" />}
                  </p>
                  <p className="text-[11px] leading-tight text-muted-foreground">
                    {t('common:holds.perNight')}
                  </p>
                </div>
                {isHeld ? (
                  <span className="shrink-0 text-xs font-semibold text-emerald-700 dark:text-emerald-300">
                    {t('desk.resolve.booked')}
                  </span>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => hold.mutate(offer)}
                    disabled={hold.isPending}
                    data-testid={`picker-hold-${offer.hotelUrn}`}
                  >
                    {hold.isPending ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : null}
                    {roomsNeeded > 1
                      ? t('desk.resolve.bookRooms', { count: roomsNeeded })
                      : t('desk.resolve.bookRoom')}
                  </Button>
                )}
              </div>
            );
          })
        )}
        {holdError ? <p className="text-xs text-destructive">{holdError}</p> : null}
      </div>

      {/* The alternatives ladder — once, with context, quiet until needed. */}
      <footer
        className={cn(
          'flex flex-wrap items-center gap-2 border-t border-border px-4 py-3',
          noInventory && 'bg-amber-50/50 dark:bg-amber-950/20',
        )}
      >
        <span className="mr-1 text-xs text-muted-foreground">
          {t('desk.resolve.alternatives')}
        </span>
        <Button
          size="sm"
          variant="outline"
          onClick={() =>
            autoAllocate.mutate({
              caseUrn: row.caseUrn,
              subCaseUrn: row.subCaseUrn,
              disruptionUrn,
            })
          }
          disabled={autoAllocate.isPending}
          title={t('desk.resolve.autoTitle')}
          data-testid="resolve-auto"
        >
          {autoAllocate.isPending ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : null}
          {t('desk.resolve.auto')}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setOutcomePath('SELF_MANAGED')}
          title={t('desk.resolve.voucherTitle')}
          data-testid="resolve-voucher"
        >
          {t('desk.resolve.voucher')}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setOutcomePath('NO_ACCOMMODATION')}
          className="text-muted-foreground hover:text-rose-600"
          title={t('desk.resolve.noOfferTitle')}
          data-testid="resolve-no-offer"
        >
          {t('desk.resolve.noOffer')}
        </Button>
      </footer>

      {outcomePath ? (
        <OutcomeDialog
          row={row}
          disruptionUrn={disruptionUrn}
          initialPath={outcomePath}
          onClose={() => setOutcomePath(null)}
        />
      ) : null}
    </section>
  );
}

export type { DisruptionPnrGroupRow };
